import React, { useEffect } from 'react';

import { Flex } from '@chakra-ui/react';

import { Navigate, Routes, useNavigate, useLocation } from 'react-router-dom';

// IMPORT ROUTE
import InventoryCount from 'routes/portal/InventoryCount';
import CreateSite from 'routes/portal/CreateSite';
import SiteDetails from 'routes/portal/SiteDetails';
import Transfers from 'routes/portal/Transfers';
import ImpactReport from 'routes/portal/ImpactReport';
import HarvestSummary from 'routes/portal/HarvestSummary';
import Groups from 'routes/portal/Groups';
import ScanDetails from 'routes/portal/ScanDetails';
import NotFound from 'routes/portal/NotFound';
import History from 'routes/portal/History';
import Employees from 'routes/portal/Employees';
import CustomerContacts from 'routes/portal/CustomerContacts';
import Customers from 'routes/portal/Customers';
import ScansTypes from 'routes/portal/ScansTypes';
import Scans from 'routes/portal/Scans';
import UnitTracking from 'routes/portal/UnitTracking';
import Sku from 'routes/portal/Sku';
import CollectionPoints from 'routes/portal/CollectionPoints';
import CustomerSites from 'routes/portal/CustomerSites';
import Pickups from 'routes/portal/Pickups';
import Deliveries from 'routes/portal/Deliveries';
import Orders from 'routes/portal/Orders';
import OrdersSummary from 'routes/portal/OrdersSummary';
import PurchaseOrders from './PurchaseOrders';
import OpenItems from 'routes/portal/OpenItems';
import WashHub from 'routes/portal/WashHub';
import Dashboard from 'routes/portal/Dashboard';
import Requests from './Requests';
import ItemsSummary from 'routes/portal/ItemsSummary';

import SideBar from 'components/core/SideBar';
import getRoute from 'components/core/RouteWithSubRoutes';

import ROUTES, { routesSettings } from 'constants/routes';
import QALog from './QALog';
import WashhubInventory from './WashhubInventory';
import Manufacturers from './Manufacturers';
import Workers from './Workers';

// import { RouteWithSubRoutesProps } from '../../../src/components/core/RouteWithSubRoutes/RouteWithSubRoutes.props';

/**
 * Items, Cases and Packages were folded into the Unit Tracking page
 * (ClickUp 86bbmb9xw). Their routes stay registered so existing bookmarks and
 * shared filter links land on the right tab instead of falling through the SPA
 * catch-all to NotFound.
 */
const UnitTrackingRedirect = ({ compType }: { compType: string }) => {
  const { search } = useLocation();

  const params = new URLSearchParams(search);
  params.set('compType', compType);

  return (
    <Navigate
      replace
      to={`${routesSettings.PORTAL_UNIT_TRACKING.path}?${params.toString()}`}
    />
  );
};

const routeConfig = Object.freeze({
  // PORTAL ROUTE ENTRY
  [ROUTES.PORTAL_INVENTORY_COUNT]: {
    element: <InventoryCount />,
    path: routesSettings.PORTAL_INVENTORY_COUNT.path,
  },
  [ROUTES.PORTAL_CREATE_SITE]: {
    element: <CreateSite />,
    path: routesSettings.PORTAL_CREATE_SITE.path,
  },
  [ROUTES.PORTAL_SITE_DETAILS]: {
    element: <SiteDetails />,
    path: routesSettings.PORTAL_SITE_DETAILS.path,
  },
  [ROUTES.PORTAL_TRANSFERS]: {
    element: <Transfers />,
    path: routesSettings.PORTAL_TRANSFERS.path,
  },
  [ROUTES.PORTAL_IMPACT_REPORT]: {
    element: <ImpactReport />,
    path: routesSettings.PORTAL_IMPACT_REPORT.path,
  },
  [ROUTES.PORTAL_HARVEST_SUMMARY]: {
    element: <HarvestSummary />,
    path: routesSettings.PORTAL_HARVEST_SUMMARY.path,
  },
  [ROUTES.PORTAL_GROUPS]: {
    element: <Groups />,
    path: routesSettings.PORTAL_GROUPS.path,
  },
  [ROUTES.PORTAL_CASES]: {
    element: <UnitTrackingRedirect compType="case" />,
    path: routesSettings.PORTAL_CASES.path,
  },
  [ROUTES.PORTAL_SCAN_DETAILS]: {
    element: <ScanDetails />,
    path: routesSettings.PORTAL_SCAN_DETAILS.path,
  },
  [ROUTES.PORTAL_HISTORY]: {
    element: <History />,
    path: routesSettings.PORTAL_HISTORY.path,
  },
  [ROUTES.PORTAL_EMPLOYEES]: {
    element: <Employees />,
    path: routesSettings.PORTAL_EMPLOYEES.path,
  },
  [ROUTES.PORTAL_CUSTOMER_CONTACTS]: {
    element: <CustomerContacts />,
    path: routesSettings.PORTAL_CUSTOMER_CONTACTS.path,
  },
  [ROUTES.PORTAL_CUSTOMERS]: {
    element: <Customers />,
    path: routesSettings.PORTAL_CUSTOMERS.path,
  },
  [ROUTES.PORTAL_SCANS_TYPES]: {
    element: <ScansTypes />,
    path: routesSettings.PORTAL_SCANS_TYPES.path,
  },
  [ROUTES.PORTAL_SCANS]: {
    element: <Scans />,
    path: routesSettings.PORTAL_SCANS.path,
  },
  [ROUTES.PORTAL_PACKAGES_CREATED]: {
    element: <UnitTrackingRedirect compType="packg" />,
    path: routesSettings.PORTAL_PACKAGES_CREATED.path,
  },
  [ROUTES.PORTAL_ITEMS]: {
    element: <UnitTrackingRedirect compType="item" />,
    path: routesSettings.PORTAL_ITEMS.path,
  },
  [ROUTES.PORTAL_UNIT_TRACKING]: {
    element: <UnitTracking />,
    path: routesSettings.PORTAL_UNIT_TRACKING.path,
  },
  [ROUTES.PORTAL_SKU]: {
    element: <Sku />,
    path: routesSettings.PORTAL_SKU.path,
  },
  [ROUTES.PORTAL_COLLECTION_POINTS]: {
    element: <CollectionPoints />,
    path: routesSettings.PORTAL_COLLECTION_POINTS.path,
  },
  [ROUTES.PORTAL_CUSTOMER_SITES]: {
    element: <CustomerSites />,
    path: routesSettings.PORTAL_CUSTOMER_SITES.path,
  },
  [ROUTES.PORTAL_PICKUPS]: {
    element: <Pickups />,
    path: routesSettings.PORTAL_PICKUPS.path,
  },
  [ROUTES.PORTAL_DELIVERIES]: {
    element: <Deliveries />,
    path: routesSettings.PORTAL_DELIVERIES.path,
  },
  [ROUTES.PORTAL_ORDERS]: {
    element: <Orders />,
    path: routesSettings.PORTAL_ORDERS.path,
  },
  [ROUTES.PORTAL_ORDERS_SUMMARY]: {
    element: <OrdersSummary />,
    path: routesSettings.PORTAL_ORDERS_SUMMARY.path,
  },
  [ROUTES.PORTAL_PURCHASE_ORDERS]: {
    element: <PurchaseOrders />,
    path: routesSettings.PORTAL_PURCHASE_ORDERS.path,
  },
  [ROUTES.PORTAL_OPEN_ITEMS]: {
    element: <OpenItems />,
    path: routesSettings.PORTAL_OPEN_ITEMS.path,
  },
  [ROUTES.PORTAL_WASH_HUB]: {
    element: <WashHub />,
    path: routesSettings.PORTAL_WASH_HUB.path,
  },
  [ROUTES.DASHBOARD]: {
    element: <Dashboard />,
    path: routesSettings.PORTAL_DASHBOARD.path,
  },
  [ROUTES.PORTAL_REQUESTS]: {
    element: <Requests />,
    path: routesSettings.PORTAL_REQUESTS.path,
  },
  [ROUTES.PORTAL_QA_LOG]: {
    element: <QALog />,
    path: routesSettings.PORTAL_QA_LOG.path,
  },
  [ROUTES.PORTAL_ITEMS_SUMMARY]: {
    element: <ItemsSummary />,
    path: routesSettings.PORTAL_ITEMS_SUMMARY.path,
  },
  [ROUTES.PORTAL_WASHUB_INVENTORY]: {
    element: <WashhubInventory />,
    path: routesSettings.PORTAL_WASHUB_INVENTORY.path,
  },
  [ROUTES.PORTAL_MANUFACTURERS]: {
    element: <Manufacturers />,
    path: routesSettings.PORTAL_MANUFACTURERS.path,
  },
  [ROUTES.PORTAL_WORKERS]: {
    element: <Workers />,
    path: routesSettings.PORTAL_WORKERS.path,
  },
  [ROUTES.PORTAL_NOT_FOUND]: {
    element: <NotFound />,
    path: routesSettings.PORTAL_NOT_FOUND.path,
  },
});

const PortalRoutes = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();

  console.log('location', location);
  const isOrderDetailsPage =
    location.pathname.includes('/orders/') && location.pathname.length > 8;
  const isSitesPage =
    location.pathname.includes('/customer-sites/') &&
    location.pathname.length > 16;

  useEffect(() => {
    const isDefaultPath = location.pathname === '/';

    if (isDefaultPath) {
      navigate(routeConfig.DASHBOARD.path, { replace: true });
    }
  }, []);

  return (
    <Flex flex={1} bg="bgColor">
      {!(isOrderDetailsPage || isSitesPage) && <SideBar />}
      <Flex
        bg="#fff"
        flexGrow={1}
        flexBasis={0}
        marginX="auto"
        overflow="auto"
        p={
          isOrderDetailsPage || isSitesPage
            ? undefined
            : { base: 2, sm: 4, md: 10 }
        }>
        <Routes>
          {Object.entries(routeConfig).map(([key, val]) => getRoute(val))}
        </Routes>
      </Flex>
    </Flex>
  );
};

export default PortalRoutes;
